import { useState, useRef, useEffect } from 'react'
import { X } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { MagicInput as Input } from '@/components/ui/magic-input'
import { Label } from '@/components/ui/label'

export interface SearchItem {
  id: string
  label: string
  /** When provided (even as empty string), an avatar is rendered with initials fallback. Omit entirely for text-only chips. */
  avatarUrl?: string
}

interface SearchMultiSelectProps {
  label: string
  placeholder?: string
  items: SearchItem[]
  selected: string[]
  onChange: (selectedIds: string[]) => void
}

export function SearchMultiSelect({
  label,
  placeholder,
  items,
  selected,
  onChange,
}: SearchMultiSelectProps) {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const selectedSet = new Set(selected)

  const filtered = items.filter(
    (item) =>
      !selectedSet.has(item.id) &&
      item.label.toLowerCase().includes(search.toLowerCase())
  )

  // Resolve selected IDs back to items, falling back to id-as-label for orphaned values
  const selectedItems: SearchItem[] = selected.map(
    (id) => items.find((item) => item.id === id) ?? { id, label: id }
  )

  function addItem(item: SearchItem) {
    onChange([...selected, item.id])
    setSearch('')
    setOpen(false)
  }

  function removeItem(id: string) {
    onChange(selected.filter((s) => s !== id))
  }

  const showAvatar = (item: SearchItem) => 'avatarUrl' in item

  return (
    <div className="space-y-2">
      <Label>{label}</Label>

      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedItems.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-1.5 rounded-full bg-muted/50 border border-border/50 pl-2 pr-1 py-0.5"
            >
              {showAvatar(item) && (
                <Avatar className="h-5 w-5 shrink-0">
                  <AvatarImage src={item.avatarUrl || undefined} alt={item.label} />
                  <AvatarFallback className="text-[10px]">
                    {item.label[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              )}
              <span className="text-xs font-medium">{item.label}</span>
              <button
                type="button"
                onClick={() => removeItem(item.id)}
                className="ml-0.5 rounded-full p-0.5 hover:bg-muted transition-colors"
                aria-label={`Remove ${item.label}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div ref={containerRef} className="relative">
        <Input
          type="text"
          placeholder={placeholder ?? `Search ${label.toLowerCase()}…`}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
        />

        {open && (
          <div className="absolute z-50 top-full mt-1 w-full rounded-md border bg-popover shadow-md max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">
                {search ? 'No matches.' : 'All options already selected.'}
              </p>
            ) : (
              filtered.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors text-left"
                  onClick={() => addItem(item)}
                >
                  {showAvatar(item) && (
                    <Avatar className="h-6 w-6 shrink-0">
                      <AvatarImage src={item.avatarUrl || undefined} alt={item.label} />
                      <AvatarFallback className="text-xs">
                        {item.label[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  )}
                  <span>{item.label}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  )
}
